/** @format */

import {
	AfterViewInit,
	Component,
	DestroyRef,
	DoCheck,
	IterableDiffer,
	IterableDiffers,
	OnInit,
	PLATFORM_ID,
	computed,
	inject,
	input,
	output
} from '@angular/core';
import {
	AnalyticsService,
	BreakpointsService,
	EmitEvent,
	EventBusService,
	Events,
	LocalStorageService,
	Scene7Service,
	UtilityService,
	WINDOW
} from '@shared/data-access/services';
import { BreakPoint, BreakPointNew } from '@shared/data-access/enums';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
	ContainerId,
	IElasticProductDetails,
	IElasticProductDetailsStyle,
	IRecommendationsRequestPurchaseTransaction,
	ProductRecommendationsPageType,
	ProductRecommendationsService
} from '@product-recommendations/data-access';
import {
	HmfButtonComponent,
	HmfCarouselComponent,
	HmfProductCardComponent,
	HmfSkeletonComponent
} from '@dsg-tech/homefield-angular';

import { ATC_BUTTON_TEXTS } from '@pdp/add-to-cart/data-access';
import { Chain } from '@shared/data-access/interfaces';
import { CheckoutPaymentPricing } from '@cart-checkout/data-access/interfaces';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'shared-components-ds-product-recommendations',
	templateUrl: './ds-product-recommendations.component.html',
	styleUrl: './ds-product-recommendations.component.scss',
	imports: [
		CommonModule,
		HmfButtonComponent,
		HmfCarouselComponent,
		HmfProductCardComponent,
		HmfSkeletonComponent
	]
})
export class DSProductRecommendationsComponent implements OnInit, AfterViewInit, DoCheck {
	private readonly destroyRef = inject(DestroyRef);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly window = inject(WINDOW) as Window;
	private readonly chain = inject(Chain);
	private readonly iterableDiffers = inject(IterableDiffers);
	private readonly analyticsService = inject(AnalyticsService);
	private readonly eventBusService = inject(EventBusService);
	private readonly localStorageService = inject(LocalStorageService);
	private readonly recommendationsService = inject(ProductRecommendationsService);
	private readonly scene7Service = inject(Scene7Service);
	private readonly utilityService = inject(UtilityService);
	private readonly breakpointsService = inject(BreakpointsService);

	/*
	 * INPUT(S)
	 */
	readonly containerID = input('');
	readonly displayName = input('Recommendations');
	readonly dsRecsDiversityLevel = input('');
	readonly eCodeList = input<string[]>([]);
	readonly eventType = input('shopping-cart-page-view');
	readonly hideAddToCart = input(false);
	readonly hasGuidVisitor = input(false);
	readonly orderPricing = input<CheckoutPaymentPricing>();
	readonly placementID = input('frequently-bought-together-no-diversity');
	readonly pageType = input(ProductRecommendationsPageType.CART);
	readonly priceRerankLevel = input('no-price-reranking');
	readonly recommendationRefreshEnabled = input(false);
	readonly showViewAllLink = input(false);
	readonly enableRepositionRecommendations = input(false);
	readonly customLinkID = input('');
	readonly capitalizeTitle = input(false);
	readonly productRecommendationsQuickViewEnabled = input(false);
	readonly showHeading = input(true);
	readonly isPLA = input(false);
	readonly smallLegacyMobileCard = input(false);
	// HMF inputs
	readonly homefieldCarouselEnabled = input(false);
	readonly showHeader = input(false);
	readonly title = input('');
	readonly showSkeletonLoading = input(false);
	readonly cardSize = input<'small' | 'medium' | 'large'>();
	readonly dsgRecs = input('');

	/*
	 * OUTPUT(S)
	 */
	recommendationsLoaded = output<boolean>();
	recommendationsErrored = output<void>();

	/*
	 * Public instance properties
	 */
	get showATC(): boolean {
		if (this.hideAddToCart()) {
			return false;
		}
		return (
			this.window.quickview?.openProductQuickView !== undefined &&
			this.window.quickview?.openProductQuickView !== null
		);
	}
	breakpoints = [BreakPoint.MEDIUM, BreakPoint.SMALL, BreakPoint.EXTRASMALL];
	eCodeListDiffer: IterableDiffer<any>;
	isFirstLoad = true;
	isMobile = computed(() => this.breakpointsService.breakpoint() <= BreakPointNew.MEDIUM);
	productRecommendations: IElasticProductDetailsStyle[] = [];
	atcButtonTexts = ATC_BUTTON_TEXTS;

	/*
	 * Private instance properties
	 */
	private athleteID: string;
	private readonly dsFilter = 'filterOutOfStockItems';
	private readonly dsPDPlanding = 'PDP_pdplanding';
	private readonly dsPDPYmal = 'PDP_YMAL';
	private readonly dsRecsGCPProject = '';
	protected dsRecsProductDisplayLimit = 10;
	private readonly onAddToCartButtonClickEventName = 'QuickViewClicked';
	private readonly onRecommendationLinkClickEventName = 'RecommendationClicked';
	private readonly onRecommendationsDisplayErrorEventName = 'DCSG_NGX_CART_RECOMMENDATIONS_ERROR';

	ngOnInit() {
		// Only do stuff client-side and if there are products
		if (isPlatformBrowser(this.platformId) && this.eCodeList()?.length > 0) {
			// Initialize eCodeList change detection
			if (this.recommendationRefreshEnabled()) {
				this.eCodeListDiffer = this.iterableDiffers.find([]).create(null);
			}

			// Get the athleteID
			this.athleteID = this.getAthleteID();

			// Clear recommendations so the skeleton shows while loading
			if (this.showSkeletonLoading()) {
				this.setRecsToEmptyForSkeletonLoading();
			}

			// Get recommendations
			this.refreshRecommendations();
		} else if (this.showSkeletonLoading() && this.isPLA()) {
			this.setRecsToEmptyForSkeletonLoading();
		}
	}

	/**
	 * @description Set the recommendations to empty for skeleton loading
	 * This will ensure that the product recommendations are not displayed until new data is received
	 */
	setRecsToEmptyForSkeletonLoading(): void {
		// Set recs to nulls
		this.productRecommendations = new Array(8).fill(null);
	}

	ngAfterViewInit(): void {
		// Check to see if QuickView exists
		if (!this.window.quickview?.openProductQuickView) {
			// Set error for analytics Event
			const error =
				'QuickViews Recommendations ATC not visible. window.quickview.openProductQuickView is not defined';
			this.analyticsService.setErrorId(this.onRecommendationsDisplayErrorEventName);
			this.analyticsService.setErrorMessage(`CART_RECOMMENDATIONS:${error}`);

			// Trigger analytics Event
			this.analyticsService.triggerAnalyticsEvent(
				this.onRecommendationsDisplayErrorEventName,
				{
					ErrorMessage: `CART_RECOMMENDATIONS: ${error}`,
					PageType: this.pageType() || 'Cart'
				}
			);
		}
		this.isFirstLoad = false;
	}

	// Listens for eCodeListchanges to refresh on item removal or addition based on list content (not reference)
	ngDoCheck(): void {
		if (this.recommendationRefreshEnabled()) {
			const eCodeChanges = this.eCodeListDiffer.diff(this.eCodeList());
			if (!this.isFirstLoad && eCodeChanges) {
				this.refreshRecommendations();
			}
		}
	}

	/**
	 * @description
	 * Event handler for when the ATC button is clicked in the product card. It will
	 * trigger an Event to analytics and then open the QuickView panel.
	 *
	 * NOTE: This method can ONLY be called if QuickView is available, because the ATC button will not be visible
	 * if QuickView is not available.
	 *
	 * @param style -- The recommended product style document
	 */
	onAddToCartButtonClick(style: IElasticProductDetailsStyle, index: number) {
		// Trigger analytics Event
		this.analyticsService.triggerAnalyticsEvent(this.onAddToCartButtonClickEventName);
		this.openQuickView(style.partNumber, index);
	}

	/**
	 * @description
	 * Event handler for the QuickView "ATC Success Event".  It will emit an Event
	 * on the Event Bus to be picked up by any listeners for additional processing.
	 */
	onQuickViewAddToCartSuccess() {
		this.eventBusService.emit(new EmitEvent(Events.PostATCEvent));
	}

	/**
	 * @description
	 * Event handler for when the recommendation product card link is clicked. It will
	 * trigger an Event to analytics and the re-assigns window.location
	 *
	 * @param style -- The recommended product style document
	 * @param index -- The index location of the product in the list
	 */
	onRecommendationLinkClick(style: IElasticProductDetailsStyle, index: any) {
		// Trigger analytics Event
		this.analyticsService.triggerAnalyticsEvent(this.onRecommendationLinkClickEventName);
		let pagetype = this.pageType();
		// Ensure the PDP URL exists
		if (typeof style.pdpSeoUrl === 'string' && style.pdpSeoUrl.trim() !== '') {
			if (
				this.pageType() == ProductRecommendationsPageType.PDP &&
				this.containerID() === ContainerId.PLA &&
				this.hideAddToCart()
			) {
				pagetype = this.dsPDPlanding;
			} else if (
				this.pageType() == ProductRecommendationsPageType.PDP &&
				this.containerID() === ContainerId.YMAL &&
				this.hideAddToCart()
			) {
				pagetype = this.dsPDPYmal;
			} else if (
				this.pageType() == ProductRecommendationsPageType.CART &&
				this.productRecommendationsQuickViewEnabled()
			) {
				this.openQuickView(style.partNumber, index);
				return;
			}
			// Build query params for analytics tracking
			const params = `recid=${this.buildRecID(pagetype, index)}`;
			// Assign window.location
			this.window.location.assign(
				`${style.pdpSeoUrl}${style.pdpSeoUrl.includes('?') ? '&' : '?'}${params}`
			);
		}
	}

	mapItemToProductCardData(item: any) {
		if (!item) return null;
		return {
			assetSeoUrl: item.pdpSeoUrl,
			ecode: item.catentryId,
			imageData: {
				imageUrl: item.image
			},
			pricing: {
				prices: {
					maxListPrice: item?.prices?.maxlistprice,
					maxOfferPrice: item?.prices?.maxofferprice,
					minListPrice: item?.prices?.minlistprice,
					minOfferPrice: item?.prices?.minofferprice,
					maxMapPrice: item?.prices?.maxmapprice
				}
			},
			productName: item.name
		};
	}

	goToRecommendedForYouDS(): void {
		// Go to Recommended For You page /s/recommended-for-you
		this.window.location.href = '/s/recommended-for-you';
	}

	getProductCardSize(): 'small' | 'medium' | 'large' {
		if (this.cardSize()) {
			return this.cardSize();
		}
		if (this.chain.chainIdentifierAbbr === 'pl') return this.isMobile() ? 'medium' : 'large';
		return this.isMobile() ? 'small' : 'medium';
	}

	private openQuickView(partNumber: string, index: number) {
		const dataAndCallBack = {
			additionalData: {
				DSDiversityLevel: this.dsRecsDiversityLevel(),
				DSDisplayName: this.displayName(),
				DSPlacementId: this.placementID(),
				DSPosition: index,
				DSPriceRerankLevel: this.priceRerankLevel(),
				DSRecsGCPProject: this.dsRecsGCPProject,
				customLinkID: this.customLinkID()
			},
			callback: this.onQuickViewAddToCartSuccess.bind(this)
		};
		this.window.quickview?.openProductQuickView(partNumber, 'DataScienceRecs', dataAndCallBack);
	}

	/**
	 * @description
	 * Checks localStorage to see if the AthleteID exists and will return it if so,
	 * otherwise returns a default value of 'XXXXX'
	 *
	 * @private
	 */
	private getAthleteID(): string | undefined {
		// The AthleteID to return, defaulted to the fallback value
		let athleteID = this.utilityService.generateUUID();
		if (this.hasGuidVisitor()) {
			return athleteID;
		}
		// Get the analytics IDs from localStorage
		const tagIDs: any = JSON.parse(this.localStorageService.get('TAG_IDs') || '{}');

		// Check to see if the AthleteID is actually set or if empty
		if (typeof tagIDs.AthleteID?.ID === 'string' && tagIDs.AthleteID.ID.trim() !== '') {
			athleteID = tagIDs.AthleteID.ID;
		} else if (
			typeof tagIDs.AdobeMCVID?.ID === 'string' &&
			tagIDs.AdobeMCVID.ID.trim() !== ''
		) {
			athleteID = tagIDs.AdobeMCVID.ID;
		}
		// Return the AthleteID
		return athleteID;
	}

	/**
	 * @description
	 * Does any product transformations before returning the results to be displayed in the template
	 *
	 * @param recommendations -- The recommendations to parse for display
	 */
	private parseRecommendations(
		recommendations: IElasticProductDetails[]
	): IElasticProductDetailsStyle[] {
		// Return array
		const returnRecommendations: IElasticProductDetailsStyle[] = [];

		// Iterate through the recommendations
		if (Array.isArray(recommendations) && recommendations?.length > 0) {
			recommendations.forEach((recommendation: IElasticProductDetails) => {
				// Get the default color if it is present
				const defaultColor =
					typeof recommendation.style.defaultColor === 'string' &&
					recommendation.style.defaultColor.trim() !== ''
						? `${recommendation.style.defaultColor}`
						: '';

				// Set the image property on the style
				recommendation.style.image = this.scene7Service.getProductImageUrl(
					recommendation.style.partNumber,
					'itembean',
					defaultColor,
					'$UTPMain$',
					undefined,
					false
				);

				recommendation.style.image = this.scene7Service.setImageWidthAndHeight(
					recommendation.style.image,
					500,
					500
				);

				// Add to the style to the display list
				returnRecommendations.push(recommendation.style);
			});
		}

		// Return the recommendations
		return returnRecommendations;
	}

	private refreshRecommendations() {
		const purchaseTransaction = {} as IRecommendationsRequestPurchaseTransaction;
		if (this.eventType() === 'purchase-complete') {
			purchaseTransaction.id = this.orderPricing().external_key;
			purchaseTransaction.revenue = this.orderPricing().total;
			purchaseTransaction.currencyCode = this.orderPricing().currency;
		}

		// Get recommendations
		this.recommendationsService
			.getRecommendationsWithProductDetails$({
				eCodeList: this.eCodeList(),
				placementId: this.placementID(),
				filter: this.dsFilter,
				priceRerankLevel: this.priceRerankLevel(),
				diversityLevel: this.dsRecsDiversityLevel(),
				eventType: this.eventType(),
				athleteId: this.athleteID,
				maxNumberOfProductsToReturn: this.dsRecsProductDisplayLimit,
				project: this.dsRecsGCPProject,
				purchaseTransaction,
				pageType: this.pageType(),
				containerID: this.containerID(),
				dsgRecs: this.dsgRecs()
			})
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (recommendations: IElasticProductDetails[]) => {
					// Parse the recommendations
					this.productRecommendations = this.parseRecommendations(recommendations);

					// Emit the event that recommendations were loaded
					this.recommendationsLoaded.emit(true);
				},
				error: () => {
					// Set error for analytics Event
					const error =
						'Error getting DS Recommendations, not displaying any recommendations';
					this.analyticsService.setErrorId(this.onRecommendationsDisplayErrorEventName);
					this.analyticsService.setErrorMessage(`CART_RECOMMENDATIONS:${error}`);

					this.productRecommendations = [];
					this.recommendationsErrored.emit();

					// Trigger analytics Event
					this.analyticsService.triggerAnalyticsEvent(
						this.onRecommendationsDisplayErrorEventName,
						{
							ErrorMessage: `CART_RECOMMENDATIONS: ${error}`,
							PageType: this.pageType() || 'Cart'
						}
					);
				}
			});
	}

	private buildRecID(pageType: string, productIndex: number): string {
		const linkID =
			this.customLinkID() !== ''
				? `DS_${pageType}_${this.customLinkID()}`
				: `DS_${pageType}_${this.placementID()}`;
		return `${linkID}_${productIndex}`;
	}

	protected trackByName(index: number, item: any): string {
		// Need to check for .name because skeleton loader requires null values and name wont exist on these values.
		return index.toString() + (item?.name ? item.name : '');
	}
}
